import { CoolLocalStorage } from '@angular-cool/storage';
import { cloneDeep, keyBy, merge, values } from 'lodash';

import { Injectable } from '@angular/core';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { IntegrationsService } from 'common/integrations-service/integrations.service';
import { LoginSubscriber } from 'shared/services/subscribers/login-subscriber.service';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';
import { IntegrationWorkItemLaneModel, WorkItemLaneModel } from './work-item-lane.model';
import { WorkItemLaneType } from './work-item-lane-type.enum';
import { BcAnalytics } from 'common/analytics/analytics.service';

import { Observable } from 'rxjs';
import { WindowService } from 'shared/services/window/window.service';
import { WindowOpenTarget } from 'shared/services/window/window-open-target.enum';
import { cannedIntegrationDescriptors } from './canned-integrations-descriptors.model';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { BcConfigService } from 'common/config/provider';

const LANE_STORAGE_KEY = 'lane-manager';

export const INTEGRATION_ID_PREFIX = 'INTEGRATIONS_';
export const RX_BUTTONS_PREFIX = 'rx-buttons';
export const RX_LANES_PREFIX = 'rx-lanes';
export const RX_LANES_TOGGLED_PREFIX = 'rx-lanes-toggled';

interface WorkItemLaneCache {
  isLoaded: boolean;

  lanes: WorkItemLaneModel[];
}

@Injectable()
export class LaneManagerService {
  public _laneCache: {};

  constructor(LogoutSubscriber: LogoutSubscriber,
              LoginSubscriber: LoginSubscriber,
              private RxEmitter: RxEmitter,
              private _integrationsService: IntegrationsService,
              private _windowService: WindowService,
              private _localStorage: CoolLocalStorage,
              private bcConfig: BcConfigService,
              private bcanalytics: BcAnalytics) {
    // Subscribe to Logout
    LoginSubscriber.subscribe(() => this._clearStorage());
    LogoutSubscriber.subscribe(() => this._clearStorage());
  }

  private static _generateButtonObservableNamespace(workItemId: string): string {
    return `${RX_BUTTONS_PREFIX}-${workItemId}`;
  }

  private static _generateLaneObservableNamespace(workItemId: string): string {
    return `${RX_LANES_PREFIX}-${workItemId}`;
  }

  private static _generateLaneToggledObservableNamespace(workItemId: string): string {
    return `${RX_LANES_TOGGLED_PREFIX}-${workItemId}`;
  }

  public static generateWorkItemObservableNamespace(messageBusEvent: string, workItemId: string): string {
    return `${messageBusEvent}-${workItemId}`;
  }

  getButtonObservable(workItemId: string): Observable<WorkItemLaneModel[]> {
    return this.RxEmitter.getNamespaceObservable(LaneManagerService._generateButtonObservableNamespace(workItemId));
  }

  getLaneObservable(workItemId: string): Observable<WorkItemLaneModel[]> {
    return this.RxEmitter.getNamespaceObservable(LaneManagerService._generateLaneObservableNamespace(workItemId));
  }

  getLaneToggledObservable(workItemId: string): Observable<WorkItemLaneModel> {
    return this.RxEmitter.getNamespaceObservable(LaneManagerService._generateLaneToggledObservableNamespace(workItemId));
  }

  initLanes(workItemId, defaultLanes: WorkItemLaneModel[], type: string, storedDefaultSettings: Object[]): void {
    this._laneCache = this._laneCache || this._loadFromStorage() || {};

    const workItemLaneCache = this._initCache(workItemId, defaultLanes);
    if (!workItemLaneCache.isLoaded) {
      this._initIntegrationLanes(workItemId, workItemLaneCache, type).then(() => {
        return this._reopenIntegrationLanes(workItemId, storedDefaultSettings, workItemLaneCache, type);
      })
        .catch(() => {
          this._saveToStorageAndSendUpdate(workItemId);
          this.RxEmitter.send(`${MessageBusEvents.SaveDefaultLaneViewToStorage}${workItemId}`, null);
        });
    } else {
      this._sendUpdate(workItemId);
    }
  }

  private _reopenIntegrationLanes(workItemId, storedDefaultSettings, workItemLaneCache, type): void {
    let promises = [];
    let mergedLanes;
    mergedLanes = this._mergeLanes(storedDefaultSettings, workItemLaneCache.lanes);
    mergedLanes.forEach((lane) => {
      if (lane.type === WorkItemLaneType.EmbeddedIntegration && lane.visible) {
        lane.visible = false;
        promises.push(this.updateIntegrationUrl(workItemId, lane, type).then(() => lane.visible = true));
      }
    });
    Promise.all(promises)
      .then(() => {
        this._saveToStorageAndSendUpdate(workItemId);
        this.RxEmitter.send(`${MessageBusEvents.SaveDefaultLaneViewToStorage}${workItemId}`, mergedLanes);
      });
  }

  private _mergeLanes(storedDefaultSettings, defaultLanes): Object[] {
    if (storedDefaultSettings) {
      const mergedLanes = values(merge(
        keyBy(defaultLanes, 'id'),
        keyBy(storedDefaultSettings, 'id')
      ));
      return mergedLanes;
    } else {
      return defaultLanes;
    }
  }

  public openLane(workItemId: string, laneId: string, type: string): void {
    const workItemLane: WorkItemLaneModel = this._getLaneById(workItemId, laneId);

    if (!workItemLane.visible) {
      this.toggleLane(workItemId, laneId, type);
    }
  }

  public closeLane(workItemId: string, laneId: string, type: string): void {
    const workItemLane: WorkItemLaneModel = this._getLaneById(workItemId, laneId);

    if (workItemLane.visible) {
      this.toggleLane(workItemId, laneId, type);
    }
  }

  toggleLane(workItemId: string, laneId: string, type: string, skipObservableUpdate: boolean = false): void {
    const workItemLane: WorkItemLaneModel = this._getLaneById(workItemId, laneId);

    if (!workItemLane) {
      return;
    }

    const integrationLane = <IntegrationWorkItemLaneModel> workItemLane;

    if (workItemLane.type === WorkItemLaneType.PopupIntegration && integrationLane.openedWindowRef && !integrationLane.openedWindowRef.closed) {
      integrationLane.openedWindowRef.focus();
    } else if (workItemLane.type === WorkItemLaneType.EmbeddedIntegration || workItemLane.type === WorkItemLaneType.PopupIntegration) {
      this.updateIntegrationUrl(workItemId, integrationLane, type).then(() => {
        toggleLaneVisibility.call(this, workItemLane);
        if (workItemLane.type === WorkItemLaneType.PopupIntegration) {
          this._openPopupIntegration(integrationLane);
        }
        if (integrationLane.visible) {
          this.bcanalytics.eventTrack('IntegrationUsed', { from: type.toLowerCase(), type: WorkItemLaneType[workItemLane.type] });
        }
      });
    } else {
      this.bcanalytics.eventTrack('IntegrationUsed', { from: type.toLowerCase(), type: WorkItemLaneType[workItemLane.type] });
      toggleLaneVisibility.call(this, workItemLane);
    }

    function toggleLaneVisibility(lane: WorkItemLaneModel): void {
      lane.visible = !lane.visible;

      this._saveToStorageAndSendUpdate(workItemId);

      if (!skipObservableUpdate) {
        this.RxEmitter.send(LaneManagerService._generateLaneToggledObservableNamespace(workItemId), lane);
      }
    }
  }

  updateIntegrationUrl(workItemId: string, integrationLane: IntegrationWorkItemLaneModel, type: string): Promise<void> {
    return this._integrationsService.getIntegrationUrl(workItemId, integrationLane.integrationId, type)
      .then(url => {
        integrationLane.src = url;
      });
  }

  private _initIntegrationLanes(workItemId: string, workItemLaneCache: WorkItemLaneCache, type: string): Promise<void> {
    return this._getIntegrationLanes(workItemId, type).then(integrationLanes => {

      Array.prototype.push.apply(workItemLaneCache.lanes, integrationLanes);
      workItemLaneCache.lanes = this._movePopupLanesToTheEnd(workItemLaneCache.lanes);

      workItemLaneCache.isLoaded = true;
    });
  }

  private _movePopupLanesToTheEnd(integrationLanes: WorkItemLaneModel[]) {
    let otherThanPopupIntegrationLanes = integrationLanes.filter(il => il.type !== WorkItemLaneType.PopupIntegration);
    let popupIntegrationLanes = integrationLanes.filter(il => il.type === WorkItemLaneType.PopupIntegration);
    return otherThanPopupIntegrationLanes.concat(popupIntegrationLanes);
  }

  private _getIntegrationLanes(workItemId: string, type: string): Promise<IntegrationWorkItemLaneModel[]> {
    return this._integrationsService.getIntegrations(workItemId, type)
      .then(integrations => {
        const extendedLanes: IntegrationWorkItemLaneModel[] = [];

        for (const integration of integrations) {
          let cannedIntegrationDescriptor;

          if (integration.cannedIntegrationId) {
            cannedIntegrationDescriptor = cannedIntegrationDescriptors.find(desc => desc.id === integration.cannedIntegrationId);

            if ((!cannedIntegrationDescriptor || !this.bcConfig.current.supportedCannedIntegrations.includes(cannedIntegrationDescriptor.name))) {
              continue;
            }
          }

          const integrationIdentifier = `${INTEGRATION_ID_PREFIX}${integration.id}`;

          const integrationLane = <IntegrationWorkItemLaneModel> {
            id: integrationIdentifier,
            integrationId: integration.id,
            type: cannedIntegrationDescriptor && cannedIntegrationDescriptor.type || (integration.isPopup ? WorkItemLaneType.PopupIntegration : WorkItemLaneType.EmbeddedIntegration),
            name: integration.name,
            width: integration.columnCount,
            visible: false,
            icon: cannedIntegrationDescriptor && cannedIntegrationDescriptor.icon,
            tooltip: integration.name,
            openedWindowRef: null
          };

          extendedLanes.push(integrationLane);
        }

        return extendedLanes;
      });
  }

  private _getLaneById(workItemId: string, laneId: string): WorkItemLaneModel {
    const laneCache: WorkItemLaneModel[] = this._getLaneCacheByWorkItem(workItemId).lanes;
    return laneCache.find(lane => lane.id === laneId);
  }

  private _getLaneCacheByWorkItem(workItemId: string): WorkItemLaneCache {
    const workItemLaneCache = this._laneCache[workItemId];

    if (!workItemLaneCache) {
      throw new Error(`WorkItem with id: "${workItemId}" not found in lane cache!`);
    }

    return workItemLaneCache;
  }

  private _setLaneByWorkItem(workItemId: string, lanes: WorkItemLaneModel[]) {
    return this._getLaneCacheByWorkItem(workItemId).lanes = lanes;
  }

  private _initCache(workItemId, defaultLanes: WorkItemLaneModel[]): WorkItemLaneCache {
    if (!this._laneCache[workItemId]) {
      let workItemLaneCache = this._laneCache[workItemId] = { lanes: [], isLoaded: false };
      this._setLaneByWorkItem(workItemId, cloneDeep(defaultLanes));
      return workItemLaneCache;
    } else {
      this._laneCache[workItemId].lanes = this._mergeCacheWithDefaultLayout(defaultLanes, this._laneCache[workItemId].lanes);
      return this._laneCache[workItemId];
    }
  }

  private _mergeCacheWithDefaultLayout(defaultLanes: WorkItemLaneModel[], laneCache: WorkItemLaneModel[]): WorkItemLaneModel[] {
    let workItemLaneCache = cloneDeep(defaultLanes);
    laneCache.forEach(lane => {
      const foundLane = workItemLaneCache.find(defaultLane => defaultLane.id === lane.id);
      if (foundLane) {
        foundLane.visible = lane.visible;
      } else if (lane.type === WorkItemLaneType.EmbeddedIntegration
        || lane.type === WorkItemLaneType.PopupIntegration
        || lane.type === WorkItemLaneType.SalesforceIntegration) {
        workItemLaneCache.push(lane);
      }
    });
    return workItemLaneCache;
  }

  private _saveToStorage() {
    this._localStorage.setObject(LANE_STORAGE_KEY, this._laneCache);
  }

  private _loadFromStorage() {
    return this._localStorage.tryGetObject(LANE_STORAGE_KEY);
  }

  private _clearStorage() {
    this._laneCache = {};
    this._localStorage.removeItem(LANE_STORAGE_KEY);
  }

  private _sendUpdate(workItemId) {
    const lanes = this._getLaneCacheByWorkItem(workItemId).lanes;
    this.RxEmitter.send(LaneManagerService._generateButtonObservableNamespace(workItemId), lanes);
    this.RxEmitter.send(LaneManagerService._generateLaneObservableNamespace(workItemId), lanes);
  }

  private _saveToStorageAndSendUpdate(workItemId) {
    this._saveToStorage();
    this._sendUpdate(workItemId);
  }

  private _openPopupIntegration(workItemLane: IntegrationWorkItemLaneModel): void {
    const openedWindow = this._windowService.open(workItemLane.src, WindowOpenTarget.Blank, {
      width: 1024,
      height: 768,
      screenX: 0,
      screenY: 0,
      top: 0,
      left: 0,
      menubar: false,
      toolbar: false,
      personalbar: false
    });

    Object.defineProperty(workItemLane, 'openedWindowRef', {
      value: openedWindow,
      enumerable: false
    });
  }
}
